import { Avatar, AvatarFallback, AvatarImage } from '@ophelos/ui/components/avatar';
import { Button } from '@ophelos/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ophelos/ui/components/dropdown-menu';
import { SidebarMenuButton, useSidebar } from '@ophelos/ui/components/sidebar';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { getAvatarFallbackClass } from '@ophelos/ui/lib/avatar-colors';
import { Cookie, EllipsisVertical, LoaderCircle, LogOut, Moon, Sun, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCookieConsentSettings } from '@/contexts/CookieConsentContext';
import { SignOutOverlay, useSignOut } from '@/hooks/useSignOut';
import { useUserProfile } from '@/hooks/useUserProfile';
import { getDisplayName, getInitials } from '@/lib/user-helpers';
import { useTheme } from '../theme/ThemeProvider';

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isSigningOut, handleSignOut } = useSignOut();
  const { theme, toggleTheme } = useTheme();
  const { profile } = useUserProfile();
  const cookieSettings = useCookieConsentSettings();

  if (!user) {
    if (isSigningOut) return <SignOutOverlay visible />;
    return null;
  }

  const userName =
    profile?.first_name || profile?.last_name ? [profile.first_name, profile.last_name].filter(Boolean).join(' ') : getDisplayName(user);
  const avatarUser = {
    name: userName,
    avatar_url: profile?.avatar_url ?? null,
    avatar_color: profile?.avatar_color ?? null,
  };

  const menuItems = (
    <>
      <DropdownMenuLabel className="p-0 font-normal">
        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
          <Avatar>
            {avatarUser.avatar_url && <AvatarImage src={avatarUser.avatar_url} alt={avatarUser.name} />}
            <AvatarFallback className={`font-medium ${getAvatarFallbackClass(avatarUser.avatar_color)}`}>
              {getInitials(avatarUser.name)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium text-foreground">{userName}</span>
            <span className="truncate text-xs text-muted-foreground">{user.email}</span>
          </div>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => navigate('/profile')}>
        <User className="mr-2 h-4 w-4 text-muted-foreground" />
        Profile
      </DropdownMenuItem>
      <DropdownMenuItem onClick={toggleTheme}>
        {theme === 'dark' ? (
          <Sun className="mr-2 h-4 w-4 text-muted-foreground" />
        ) : (
          <Moon className="mr-2 h-4 w-4 text-muted-foreground" />
        )}
        {theme === 'dark' ? 'Light mode' : 'Dark mode'}
      </DropdownMenuItem>
      {cookieSettings && (
        <DropdownMenuItem onClick={cookieSettings.openSettings}>
          <Cookie className="mr-2 h-4 w-4 text-muted-foreground" />
          Cookie settings
        </DropdownMenuItem>
      )}

      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={(e) => {
          e.preventDefault();
          handleSignOut();
        }}
        disabled={isSigningOut}
      >
        {isSigningOut ? (
          <LoaderCircle className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <LogOut className="mr-2 h-4 w-4 text-muted-foreground" />
        )}
        {isSigningOut ? 'Signing out...' : 'Sign out'}
      </DropdownMenuItem>
    </>
  );

  if (compact) {
    return (
      <>
        <SignOutOverlay visible={isSigningOut} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="rounded-full">
              <Avatar>
                {avatarUser.avatar_url && <AvatarImage src={avatarUser.avatar_url} alt={avatarUser.name} />}
                <AvatarFallback className={`font-medium ${getAvatarFallbackClass(avatarUser.avatar_color)}`}>
                  {getInitials(avatarUser.name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 rounded-lg" side="right" align="end" sideOffset={4}>
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  // Delegate to sidebar-aware variant (useSidebar is only called inside SidebarProvider)
  return <SidebarUserMenu user={user} userName={userName} menuItems={menuItems} isSigningOut={isSigningOut} avatarUser={avatarUser} />;
}

/** Sidebar-aware trigger + dropdown positioning. Must be rendered inside a SidebarProvider. */
function SidebarUserMenu({
  user,
  userName,
  menuItems,
  isSigningOut,
  avatarUser,
}: {
  user: { email: string };
  userName: string;
  menuItems: React.ReactNode;
  isSigningOut: boolean;
  avatarUser: { name: string; avatar_url: string | null; avatar_color: string | null };
}) {
  const { isMobile, state } = useSidebar();

  return (
    <>
      <SignOutOverlay visible={isSigningOut} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="h-full!">
          {state === 'collapsed' ? (
            <Button variant="ghost" size="icon-sm">
              <Avatar>
                {avatarUser.avatar_url && <AvatarImage src={avatarUser.avatar_url} alt={avatarUser.name} />}
                <AvatarFallback className={`font-medium ${getAvatarFallbackClass(avatarUser.avatar_color)}`}>
                  {getInitials(avatarUser.name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          ) : (
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              data-testid="user-menu-button"
            >
              <Avatar>
                {avatarUser.avatar_url && <AvatarImage src={avatarUser.avatar_url} alt={avatarUser.name} />}
                <AvatarFallback className={`font-medium ${getAvatarFallbackClass(avatarUser.avatar_color)}`}>
                  {getInitials(avatarUser.name)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{userName}</span>
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              </div>
              <EllipsisVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
